import React, { useState, useEffect, useContext, Fragment } from 'react'
import { Separator, Expander, ExpanderContent, SocialBar, IconButton } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import { useQuery } from '@apollo/client'
import { css } from '@emotion/core'
import { ArrowCaret } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { useRouteMatch, useLocation } from '@fs/zion-router'
import Head, { Title } from '@fs/zion-head'
import useIsMobile from '../../hooks/useIsMobile'
import ContentCardRow from '../ContentCardRow'
import SeriesVideoRow from './SeriesVideoRow'
import { PlaylistContext } from '../PlaylistContext'
import DownloadableResources from '../DownloadableResources'
import TopDetailBar from '../TopDetailBar'
import getRelativeUrl from '../../helpers/UrlHelper'
import api from '../../api/brightspot'
import SpotlightGradient from '../SpotlightGradient'
import LargeLoadingSpinner from '../LargeLoadingSpinner'
import SessionView from '../session-view/SessionView'
import SectionHeader from '../SectionHeader'
import { H4, H6 } from '../type/type'
import { markItemsInPlaylist } from '../../global/playlistHelper'
import { getLanguageFromLocale } from '../../helpers/languageNameInLanguage'
import RichTextDisplayer from '../RichTextDisplayer'
import getCreatorsNames from '../../helpers/creatorsNames'
import BS_OBJECT_TYPES from '../../api/brightspot/objectTypeIds'
import getIsLive from '../../helpers/isLiveHelper'

const SeriesSet = ({ content, selectedYoutubeId }) => {
  const { playlist } = useContext(PlaylistContext)
  const markedItems = markItemsInPlaylist({ playlist, items: content })
  return markedItems?.map((contentItem) => (
    <SeriesVideoRow
      spokenLanguage={getLanguageFromLocale({
        locale: contentItem?.locale,
        parentLocale: contentItem?.parentLocale,
      })}
      key={contentItem?.contentUUID}
      isSelected={contentItem.youTubeId === selectedYoutubeId}
      image={
        contentItem?.image?.custom?.image?.src ??
        contentItem?.creators?.[0]?.profilePhoto?.custom?.image?.src ??
        contentItem?.profilePhoto?.custom?.image?.src
      }
      headingText={contentItem?.title}
      presenterText={getCreatorsNames(contentItem?.creators)}
      detailText={contentItem?.description}
      durationText={contentItem?.durationText}
      isLive={getIsLive(contentItem)}
      goLivetime={contentItem?.date?.timestamp}
      url={getRelativeUrl(contentItem.url)}
      contentUUID={contentItem.contentUUID}
      isInPlaylist={contentItem.isInPlaylist}
    />
  ))
}

const SeriesRow = ({ content, selectedYoutubeId, contentItem }) => {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(true)
  return content.length > 1 ? (
    <Expander open={isOpen} setOpen={setIsOpen}>
      <SectionHeader
        heading={contentItem.title}
        description={contentItem?.description}
        small={isMobile}
        // internally we hide this button from screen readers
        // because we have it open by default and opening/closing it is not needed for them
        button={
          <IconButton
            size={isMobile ? 'sm' : 'md'}
            Icon={ArrowCaret}
            iconDirection={isOpen ? 'up' : 'down'}
            onClick={() => setIsOpen(!isOpen)}
          />
        }
      />
      <ExpanderContent>
        <SeriesSet content={contentItem.items} selectedYoutubeId={selectedYoutubeId} />
      </ExpanderContent>
    </Expander>
  ) : (
    <>
      {content.length > 1 && (
        <>
          <H4 as="h2">{contentItem.title}</H4>
          <H6 as="h3">
            <RichTextDisplayer richText={contentItem.description} />
          </H6>
        </>
      )}
      <SeriesSet content={contentItem.items} selectedYoutubeId={selectedYoutubeId} />
    </>
  )
}

const barOptionsCss = css`
  color: white;
  display: flex;
  align-items: center;

  & button[iconsize] {
    margin-bottom: 0 !important;
  }
`

const shareTextCss = css`
  margin-right: 16px;
  font-size: 14px;
`

const SeriesDetail = ({ previewId }) => {
  const [t] = useTranslation()
  const { playlist } = useContext(PlaylistContext)
  const [isInPlaylist, setIsInPlaylist] = useState(false)
  const isMobile = useIsMobile()
  const routeMatch = useRouteMatch('/series/:id')
  const pathOrId = previewId || `/series/${routeMatch?.params.id}`
  const location = useLocation()
  const { data, loading } = useQuery(api.series.seriesGQLQuery(pathOrId))

  usePageView({ page_detail: `${location.pathname}` })

  useEffect(() => {
    if (playlist) {
      const match = playlist.find((item) => {
        return item.cmsId === data?.RootsTechPageEntry?.contentId
      })
      if (match) {
        setIsInPlaylist(true)
      } else {
        setIsInPlaylist(false)
      }
    }
  }, [data, playlist])

  if (loading) {
    return <LargeLoadingSpinner />
  }

  const separatorSize = isMobile ? 'lg' : 'xxl'
  // To Do: Get brightstpot to stop sending tags in with suggested content
  const suggestedContent = data?.RootsTechPageEntry?.suggestedContent?.filter((item) => {
    return item.objectTypeId !== BS_OBJECT_TYPES.TAG
  })

  return (
    <>
      <Title breadcrumbs={[t('site-title', 'RootsTech 2021'), data?.RootsTechPageEntry?.title || '']}>
        {t('playlistpage.series', 'Series')}
      </Title>

      <Head>
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:title"
          content={`${data?.RootsTechPageEntry?.title} \u2022 ${t(
            'site-title',
            'RootsTech 2021'
          )} \u2022 FamilySearch `}
        />
        <meta property="og:description" content={data?.RootsTechPageEntry?.description} />
        <meta property="og:image" content={data?.RootsTechPageEntry?.mainImage?.custom?.image?.src} />
      </Head>

      <SpotlightGradient flavor="apple" size={isMobile ? 'sm' : 'lg'} />

      <SessionView
        nav={
          <TopDetailBar backText={t('common-ui:back.action', 'Back')} backLink="/series">
            <div css={barOptionsCss}>
              {!isMobile && (
                <div css={shareTextCss}>{t('series.share-with-others.label', 'Share this series with others')}</div>
              )}
              <SocialBar
                useIconButtons
                apps={['facebook', 'twitter', 'whatsApp']}
                sharable={{
                  url: encodeURIComponent(window.location.href),
                  subject: t('series.sharing-subject.label', 'Rootstech Series: {title}', {
                    title: data?.RootsTechPageEntry?.title,
                  }),
                }}
              />
            </div>
          </TopDetailBar>
        }
        isSeries
        data={data?.RootsTechPageEntry}
        loading={loading}
        profilePhoto={data?.RootsTechPageEntry?.mainImage?.custom?.image?.src}
        heading={data?.RootsTechPageEntry?.title}
        description={data?.RootsTechPageEntry?.description}
        videoProvider={data?.RootsTechPageEntry?.overviewVideo?.videoProvider}
        isInPlaylist={isInPlaylist}
      >
        <>
          {data?.RootsTechPageEntry?.sections?.map((contentItem) => (
            <Fragment key={contentItem.title}>
              <Separator size={separatorSize} />
              <SeriesRow content={data?.RootsTechPageEntry?.sections} contentItem={contentItem} />
            </Fragment>
          ))}

          {data?.RootsTechPageEntry?.sections.length > 0 && <Separator size={isMobile ? 'lg' : 'xl'} />}

          {data?.RootsTechPageEntry?.additionalExperiences?.length > 0 && (
            <>
              <ContentCardRow
                headingText={t('series.additional-experiences', 'Additional Experiences')}
                useHeadingDivider
                items={data?.RootsTechPageEntry?.additionalExperiences}
                isPlaylistable={false}
                openInNewTab
              />
            </>
          )}

          {data?.RootsTechPageEntry?.downloadableResources?.length > 0 && (
            <>
              <Separator size={separatorSize} />
              <DownloadableResources downloadableResources={data?.RootsTechPageEntry?.downloadableResources} />
            </>
          )}

          <Separator size={separatorSize} />

          {suggestedContent && (
            <ContentCardRow
              headingText={t('series.suggested-content', 'Suggested Content')}
              useHeadingDivider
              // Limit number of suggested content items to 8
              items={suggestedContent.slice(0, 8)}
              isPlaylistable={false}
              tags={data?.RootsTechPageEntry?.keywords}
            />
          )}

          <Separator size={separatorSize} />
        </>
      </SessionView>
    </>
  )
}

export default SeriesDetail
